import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import {
  drive_get_file_permissions_path,
  drive_set_file_permissions_path,
  drive_get_folder_permissions_path,
  drive_set_folder_permissions_path,
  share_targets_drive_index_path
} from '../../routes'
import { useBootstrapModal, csrfToken } from '../../modules/drive/bootstrap-modal.js'
import { useDriveI18n } from './drive-context.js'

export function PermissionsModal({ open, target, onClose }) {
  const i18n = useDriveI18n()
  const t = (key) => i18n.t(key)

  const [people, setPeople] = useState([])
  const [groups, setGroups] = useState([])
  const [selectedPeople, setSelectedPeople] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [clearAll, setClearAll] = useState(false)
  const [phase, setPhase] = useState('form')
  const [busy, setBusy] = useState(false)

  const handleHidden = useCallback(() => onClose?.(), [onClose])
  const modalRef = useBootstrapModal(open, handleHidden)

  useEffect(() => {
    if (!open || !target?.id) return
    setPhase('form')
    setClearAll(false)
    setBusy(false)

    Promise.all([
      fetch(share_targets_drive_index_path({ format: 'json' }), {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() }
      }).then((r) => r.json()),
      fetch(
        target.type === 'folder'
          ? drive_get_folder_permissions_path(target.id)
          : drive_get_file_permissions_path(target.id),
        { headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() } }
      ).then((r) => r.json())
    ]).then(([targets, perms]) => {
      setPeople(targets.people || [])
      setGroups(targets.groups || [])
      setSelectedPeople((perms.person_ids || []).map(String))
      setSelectedGroups((perms.person_group_ids || []).map(String))
    }).catch((e) => console.error(e))
  }, [open, target])

  async function save() {
    if (!target?.id) return
    setBusy(true)
    try {
      const url = target.type === 'folder'
        ? drive_set_folder_permissions_path(target.id)
        : drive_set_file_permissions_path(target.id)
      const body = clearAll
        ? { person_ids: [], person_group_ids: [] }
        : { person_ids: selectedPeople, person_group_ids: selectedGroups }
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken()
        },
        body: JSON.stringify(body)
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setPhase('result')
    } catch (e) {
      console.error(e)
      alert(t('drive.error'))
    } finally {
      setBusy(false)
    }
  }

  const title = target?.type === 'folder' ? t('drive.folder_permissions') : t('drive.file_permissions')

  return (
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref={modalRef}>
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bi bi-shield-lock me-2" />{title}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label={t('helpers.close')} />
          </div>
          <div class="modal-body">
            <div class="alert alert-info"><i class="bi bi-info-circle me-2" />{t('drive.permissions_info')}</div>
            {phase === 'form' ? (
              <>
                <div class="mb-3">
                  <label class="form-label fw-bold">{t('drive.allow_access_to_people')}</label>
                  <select class="form-select" multiple size="8" disabled={clearAll} value={selectedPeople} onChange={(e) => setSelectedPeople([...e.target.selectedOptions].map((o) => o.value))}>
                    {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <div class="form-text">{t('drive.select_multiple_hint')}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label fw-bold">{t('drive.allow_access_to_groups')}</label>
                  <select class="form-select" multiple size="5" disabled={clearAll} value={selectedGroups} onChange={(e) => setSelectedGroups([...e.target.selectedOptions].map((o) => o.value))}>
                    {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                  <div class="form-text">{t('drive.select_multiple_hint')}</div>
                </div>
                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="permissions-clear-all-p" checked={clearAll} onChange={(e) => setClearAll(e.target.checked)} />
                  <label class="form-check-label" for="permissions-clear-all-p">{t('drive.clear_all_permissions')}</label>
                  <div class="form-text">{t('drive.clear_permissions_hint')}</div>
                </div>
              </>
            ) : (
              <div class="alert alert-success"><i class="bi bi-check-circle me-2" />{t('drive.permissions_updated')}</div>
            )}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{t('helpers.close')}</button>
            {phase === 'form' && (
              <button type="button" class="btn btn-primary" disabled={busy} onClick={save}>{t('drive.save_permissions')}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
